/*
 * Copyright (c) 2025.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import {useEffect, useRef, useState} from 'react';
import {Mousetrap} from "shortcuts";
import {create} from "zustand";
import useEvent from "./useEvent.jsx";
import Unique from 'toolbox/unique';

export const useShortcutsStore = create((set) => ({
    shortcuts: [],

    addShortcut: (newShortcut) => set((state) => {
        const exists = state.shortcuts.some(item => item.sourceId === newShortcut.sourceId);

        if (exists) return state;

        return {shortcuts: [...state.shortcuts, newShortcut]};
    }),
    removeShortcuts: (sourceId) => set((state) => ({
        shortcuts: state.shortcuts.filter(item => item.sourceId !== sourceId)
    })),
}))

export const getShortcutsStore = () => useShortcutsStore.getState()

function useShortcuts() {
    const [sourceId] = useState(Unique.newId())
    const bindingsRef = useRef([]);

    function register(label = "", categoryName = "", shortcut, callback) {
        const {shortcuts, addShortcut} = getShortcutsStore();

        const exists = shortcuts.some(item => item.sourceId === sourceId);

        if (!exists) {
            addShortcut({
                sourceId,
                label: label.toString().trim(),
                shortcut,
                categoryName: categoryName.toString().trim()
            });

            if (callback !== undefined) {
                // Bind the shortcut using Mousetrap
                let unbindFn = Mousetrap.bind(shortcut, callback);
                bindingsRef.current.push(unbindFn);
            }
        }
    }

    useEffect(() => {
        return () => {
            getShortcutsStore().removeShortcuts(sourceId);

            bindingsRef.current.forEach(unbind => {
                if (typeof unbind === 'function') unbind();
            });
            bindingsRef.current = [];
        };
    }, [sourceId]);

    return {
        register,
    };
}


function useRegisterShortcut(label = "", categoryName = "", shortcut, callback) {
    const [sourceId] = useState(Unique.newId())
    const bindingsRef = useRef([]);
    const scopedCallback = useEvent(callback);

    if (!Array.isArray(shortcut)) {
        shortcut = [shortcut]
    }

    useEffect(() => {
        const {shortcuts, addShortcut} = getShortcutsStore();

        const exists = shortcuts.some(item => item.label === label && item.sourceId === sourceId);

        if (!exists) {
            addShortcut({
                sourceId,
                label: label.toString().trim(),
                shortcut,
                categoryName: categoryName.toString().trim()
            });

            if (callback !== undefined) {
                let unbindFn = Mousetrap.bind(shortcut, scopedCallback);
                bindingsRef.current.push(unbindFn);
            }
        }

        return () => {

            getShortcutsStore().removeShortcuts(sourceId);

            bindingsRef.current.forEach(unbind => {
                if (typeof unbind === 'function') unbind();
            });
            bindingsRef.current = [];
        };
    }, [label, categoryName, shortcut, callback, scopedCallback, sourceId]);

    return null;
}

export {useShortcuts, useRegisterShortcut};